/**
 * AdminOAuthCallback.js (Lane 1416)
 *
 * Landing page for the admin Google sign-in. The backend callback redirects
 * here with a single-use ?code=; this page exchanges it at
 * POST /api/admin/oauth/exchange for the standard admin {token, user} and
 * stores it through the same AuthContext path as every other sign-in. The
 * token itself never appears in a URL. Mirrors SsoCallback.js.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';

function AdminOAuthCallback() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  // StrictMode runs effects twice in dev; the code is single-use server-side.
  const exchangeStarted = useRef(false);

  useEffect(() => {
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      navigate('/admin/login?oauth_error=auth_failed', { replace: true });
      return;
    }

    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/oauth/exchange`, {
          method: 'POST',
          // The code is bound to an HttpOnly cookie set at the start of
          // sign-in; the exchange must carry it (local dev is cross-origin).
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const data = await response.json();
        if (!response.ok || !data.token || !data.user || data.user.user_type !== 'admin') {
          setFailed(true);
          navigate('/admin/login?oauth_error=expired', { replace: true });
          return;
        }
        loginWithToken(data.token, data.user);
        navigate('/admin', { replace: true });
      } catch (err) {
        setFailed(true);
        navigate('/admin/login?oauth_error=unavailable', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#8A9BAE',
      fontSize: 'var(--font-size-md)',
    }}>
      {failed ? 'Returning to sign-in…' : 'Completing sign-in…'}
    </div>
  );
}

export default AdminOAuthCallback;
